import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  Wifi, 
  Router, 
  Smartphone, 
  Printer, 
  Camera, 
  Server,
  HardDrive,
  CheckCircle2,
  XCircle,
  MoreVertical,
  Download,
  Filter,
  Cloud,
  Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const NonAgentAssets = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [devices, setDevices] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterSite, setFilterSite] = useState('all');

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const response = await api.get('/non-agent-assets');
      setDevices(response.data);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromUniFi = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/non-agent-assets/sync-unifi');
      toast.success(`Synced ${response.data.devices_count} devices from UniFi`);
      await fetchDevices();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to sync from UniFi';
      toast.error(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  const handleConvertToManaged = async (deviceId) => {
    try {
      await api.post(`/non-agent-assets/${deviceId}/convert-to-managed`);
      toast.success('Device converted to managed asset');
      await fetchDevices();
    } catch (error) {
      toast.error('Failed to convert device');
    }
  };

  const handleConvertToNonManaged = async (deviceId) => {
    try {
      await api.post(`/non-agent-assets/${deviceId}/convert-to-non-managed`);
      toast.success('Device converted to non-managed asset');
      await fetchDevices();
    } catch (error) {
      toast.error('Failed to convert device');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'access_point': return <Wifi className="w-5 h-5" />;
      case 'switch': return <Server className="w-5 h-5" />;
      case 'router': return <Router className="w-5 h-5" />;
      case 'phone': return <Smartphone className="w-5 h-5" />;
      case 'printer': return <Printer className="w-5 h-5" />;
      case 'camera': return <Camera className="w-5 h-5" />;
      default: return <HardDrive className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'access_point': return 'text-blue-600 bg-blue-100';
      case 'switch': return 'text-purple-600 bg-purple-100';
      case 'router': return 'text-green-600 bg-green-100';
      case 'phone': return 'text-orange-600 bg-orange-100';
      case 'printer': return 'text-pink-600 bg-pink-100';
      case 'camera': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Get unique sites from devices
  const uniqueSites = [...new Set(devices.filter(d => d.unifi_host_name).map(d => d.unifi_host_name))];

  const filteredDevices = devices.filter(device => {
    // Use asset_type instead of category for filtering
    const deviceCategory = device.asset_type || device.category;
    if (filterCategory !== 'all' && deviceCategory !== filterCategory) return false;
    if (filterStatus !== 'all' && device.status !== filterStatus) return false;
    if (filterSource !== 'all' && device.source !== filterSource) return false;
    if (filterSite !== 'all' && device.unifi_host_name !== filterSite) return false;
    return true;
  });

  // Count by category
  const categoryCounts = devices.reduce((acc, d) => {
    const cat = d.asset_type || d.category || 'other';
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  const categories = [
    { value: 'all', label: 'All Devices' },
    { value: 'access_point', label: `Access Points (${categoryCounts.access_point || 0})` },
    { value: 'switch', label: `Switches (${categoryCounts.switch || 0})` },
    { value: 'router', label: `Routers (${categoryCounts.router || 0})` },
    { value: 'camera', label: `Cameras (${categoryCounts.camera || 0})` },
    { value: 'server', label: `Servers (${categoryCounts.server || 0})` },
    { value: 'printer', label: `Printers (${categoryCounts.printer || 0})` },
    { value: 'network_device', label: `Other Network (${categoryCounts.network_device || 0})` },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Non-Agent Assets</h1>
            <p className="text-sm text-gray-600 mt-1">
              Network devices discovered from UniFi Controller
            </p>
          </div>
          <Button
            onClick={handleSyncFromUniFi}
            disabled={syncing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Sync from UniFi
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Source
            </label>
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Sources</option>
              <option value="unifi">UniFi</option>
              <option value="manual">Manual</option>
            </select>
          </div>
          {uniqueSites.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Site/Host
              </label>
              <select
                value={filterSite}
                onChange={(e) => setFilterSite(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Sites</option>
                {uniqueSites.map(site => (
                  <option key={site} value={site}>{site}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              Showing {filteredDevices.length} of {devices.length} devices
            </div>
          </div>
        </div>
      </div>

      {/* Device List */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
            <p className="text-gray-600">Loading devices...</p>
          </div>
        ) : filteredDevices.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Wifi className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">
                {devices.length === 0 
                  ? 'No devices found. Go to Settings → UniFi Site Manager to sync devices.'
                  : 'No devices match the selected filters.'}
              </p>
              <Button onClick={() => navigate('/settings/unifi-controllers')} variant="outline">
                Configure UniFi Site Manager
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredDevices.map((device) => {
              const deviceCategory = device.asset_type || device.category;
              return (
                <Card key={device.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded-lg ${getCategoryColor(deviceCategory)}`}>
                        {getCategoryIcon(deviceCategory)}
                      </div>
                      <div className="flex items-center gap-2">
                        {device.source === 'unifi' && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <Cloud className="w-3 h-3" />
                            UniFi
                          </Badge>
                        )}
                        {device.status === 'online' ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-1 truncate" title={device.name || device.hostname}>
                      {device.name || device.hostname || 'Unnamed Device'}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2 capitalize">
                      {deviceCategory?.replace('_', ' ')}
                    </p>
                    
                    {/* UniFi Host/Site Badge */}
                    {device.unifi_host_name && (
                      <div className="mb-3">
                        <Badge variant="secondary" className="text-xs gap-1">
                          <Globe className="w-3 h-3" />
                          {device.unifi_host_name}
                        </Badge>
                      </div>
                    )}

                    <div className="space-y-1.5 mb-4 text-xs">
                      {device.ip_address && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">IP:</span>
                          <span className="text-gray-900 font-mono">{device.ip_address}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">MAC:</span>
                        <span className="text-gray-900 font-mono">{device.mac_address}</span>
                      </div>
                      {device.model && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Model:</span>
                          <span className="text-gray-900 truncate ml-2" title={device.model}>{device.model}</span>
                        </div>
                      )}
                      {device.firmware_version && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Firmware:</span>
                          <span className="text-gray-900">{device.firmware_version}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleConvertToManaged(device.id)}
                        className="flex-1 text-xs"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        To Managed
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleConvertToNonManaged(device.id)}
                        className="flex-1 text-xs"
                      >
                        To Inventory
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NonAgentAssets;
